import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faSearch,
    faSpinner,
    faExclamationTriangle,
    faCheckCircle,
    faInfoCircle,
    faCopy,
} from '@fortawesome/free-solid-svg-icons';
import adminSecurityService from '../../../helpers/adminSecurityService';

const SpamCleanup = () => {
    const [step, setStep] = useState(1); // 1: Search, 2: Review, 3: Confirm
    const [formData, setFormData] = useState({
        pattern: '',
        searchInNames: true,
        searchInEmails: false,
        marcaId: '',
        dryRun: true,
    });
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [error, setError] = useState(null);
    const [confirmed, setConfirmed] = useState(false);

    const predefinedPatterns = [
        { pattern: '* * * 🏆', label: 'Spam ruso común' },
        { pattern: 'http://', label: 'URLs en nombres' },
        { pattern: 'https://', label: 'URLs seguras en nombres' },
        { pattern: '🏆 Поздравляем', label: 'Spam ruso con emojis' },
        { pattern: '\\* \\* \\*', label: 'Múltiples asteriscos' },
    ];

    const handlePredefinedPattern = (pattern) => {
        setFormData({ ...formData, pattern });
    };

    const handleSearch = async () => {
        if (!formData.pattern.trim()) {
            setError('El patrón de búsqueda es requerido');
            return;
        }

        setLoading(true);
        setError(null);
        setSearchResults(null);

        try {
            const results = await adminSecurityService.deleteClientsByPattern(formData.pattern, {
                searchInNames: formData.searchInNames,
                searchInEmails: formData.searchInEmails,
                marcaId: formData.marcaId || null,
                dryRun: true, // Siempre hacer dry-run primero
            });
            setSearchResults(results);
            setStep(2);
        } catch (err) {
            console.error('Error searching clients:', err);
            setError(err.message || 'Error al buscar clientes');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmed) {
            setError('Debes confirmar que entiendes que esta acción es irreversible');
            return;
        }

        if (
            !window.confirm(
                `¿Estás ABSOLUTAMENTE seguro de que deseas eliminar ${searchResults.found} clientes? Esta acción NO se puede deshacer.`
            )
        ) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const results = await adminSecurityService.deleteClientsByPattern(formData.pattern, {
                searchInNames: formData.searchInNames,
                searchInEmails: formData.searchInEmails,
                marcaId: formData.marcaId || null,
                dryRun: false, // Ahora sí eliminar
            });
            setSearchResults(results);
            setStep(3);
        } catch (err) {
            console.error('Error deleting clients:', err);
            setError(err.message || 'Error al eliminar clientes');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setStep(1);
        setSearchResults(null);
        setError(null);
        setConfirmed(false);
    };

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            {/* Warning Card */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                    <div className="text-sm text-red-800">
                        <p className="font-medium mb-1">⚠️ Acción Destructiva</p>
                        <p>
                            Esta herramienta elimina clientes permanentemente. Siempre usa <strong>dry-run</strong>{' '}
                            primero para revisar qué se eliminará. Esta acción NO se puede deshacer.
                        </p>
                    </div>
                </div>
            </div>

            {/* Step 1: Search */}
            {step === 1 && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={faSearch} className="text-red-600 text-xl" />
                            <h3 className="text-xl font-semibold text-gray-900">Buscar Clientes Spam</h3>
                        </div>
                        <p className="text-sm text-gray-600 mt-2">
                            Busca clientes que coincidan con un patrón específico
                        </p>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Pattern Input */}
                        <div>
                            <label htmlFor="pattern" className="block text-sm font-medium text-gray-700 mb-2">
                                Patrón de Búsqueda <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="pattern"
                                value={formData.pattern}
                                onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                                placeholder="Ej: * * * 🏆"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Los patrones son case-insensitive y usan regex de MongoDB
                            </p>

                            {/* Predefined Patterns */}
                            <div className="mt-3">
                                <p className="text-xs text-gray-500 mb-2">Patrones predefinidos:</p>
                                <div className="flex flex-wrap gap-2">
                                    {predefinedPatterns.map((item) => (
                                        <button
                                            key={item.pattern}
                                            type="button"
                                            onClick={() => handlePredefinedPattern(item.pattern)}
                                            className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Search Options */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-3">
                                Opciones de Búsqueda
                            </label>
                            <div className="space-y-2">
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.searchInNames}
                                        onChange={(e) =>
                                            setFormData({ ...formData, searchInNames: e.target.checked })
                                        }
                                        className="mr-2"
                                    />
                                    <span className="text-sm text-gray-700">Buscar en nombres</span>
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={formData.searchInEmails}
                                        onChange={(e) =>
                                            setFormData({ ...formData, searchInEmails: e.target.checked })
                                        }
                                        className="mr-2"
                                    />
                                    <span className="text-sm text-gray-700">Buscar en emails</span>
                                </label>
                            </div>
                        </div>

                        {/* Marca Filter (opcional) */}
                        <div>
                            <label htmlFor="marcaId" className="block text-sm font-medium text-gray-700 mb-2">
                                Filtrar por Marca (opcional)
                            </label>
                            <input
                                type="text"
                                id="marcaId"
                                value={formData.marcaId}
                                onChange={(e) => setFormData({ ...formData, marcaId: e.target.value })}
                                placeholder="ID de marca (dejar vacío para todas)"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                            />
                        </div>

                        {/* Search Button */}
                        <div className="flex justify-end">
                            <button
                                onClick={handleSearch}
                                disabled={loading || !formData.pattern.trim()}
                                className={`
                  flex items-center gap-2 px-6 py-2 rounded-md font-medium transition-colors
                  ${loading || !formData.pattern.trim()
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }
                `}
                            >
                                {loading ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                        Buscando...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSearch} />
                                        Buscar (Dry Run)
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 2: Review Results */}
            {step === 2 && searchResults && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-xl font-semibold text-gray-900">Resultados de Búsqueda</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Se encontraron <strong>{searchResults.found}</strong> clientes que coinciden con el
                                    patrón
                                </p>
                            </div>
                            <button
                                onClick={handleReset}
                                className="text-sm text-gray-600 hover:text-gray-900"
                            >
                                Nueva búsqueda
                            </button>
                        </div>
                    </div>

                    <div className="p-6">
                        {searchResults.clients && searchResults.clients.length > 0 ? (
                            <>
                                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                                    <div className="flex items-start gap-2">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-600 mt-0.5" />
                                        <div className="text-sm text-yellow-800">
                                            <strong>Advertencia:</strong> Estos clientes serán eliminados permanentemente si
                                            procedes. Esta acción NO se puede deshacer.
                                        </div>
                                    </div>
                                </div>

                                <div className="overflow-x-auto mb-6">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                    Nombre
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                    Email
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                    Marca
                                                </th>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                    Fecha Creación
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {searchResults.clients.slice(0, 50).map((client) => (
                                                <tr key={client.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        {client.nombre} {client.apellido}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{client.email}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{client.marca || '-'}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                        {client.fechaCreacion
                                                            ? new Date(client.fechaCreacion).toLocaleDateString('es-ES')
                                                            : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {searchResults.clients.length > 50 && (
                                        <p className="mt-2 text-sm text-gray-600 text-center">
                                            Mostrando 50 de {searchResults.clients.length} resultados
                                        </p>
                                    )}
                                </div>

                                <div className="border-t border-gray-200 pt-6 space-y-4">
                                    <label className="flex items-start">
                                        <input
                                            type="checkbox"
                                            checked={confirmed}
                                            onChange={(e) => setConfirmed(e.target.checked)}
                                            className="mt-1 mr-2"
                                        />
                                        <span className="text-sm text-gray-700">
                                            Entiendo que esta acción eliminará permanentemente{' '}
                                            <strong>{searchResults.found}</strong> clientes y NO se puede deshacer.
                                        </span>
                                    </label>

                                    <div className="flex justify-end gap-3">
                                        <button
                                            onClick={handleReset}
                                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            disabled={!confirmed || loading}
                                            className={`
                        flex items-center gap-2 px-6 py-2 rounded-md font-medium transition-colors
                        ${!confirmed || loading
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-red-600 text-white hover:bg-red-700'
                                                }
                      `}
                                        >
                                            {loading ? (
                                                <>
                                                    <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                                                    Eliminando...
                                                </>
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                    Eliminar {searchResults.found} Clientes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-12">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-500 mb-4" />
                                <p className="text-gray-600">No se encontraron clientes que coincidan con el patrón</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Step 3: Success */}
            {step === 3 && searchResults && (
                <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                    <div className="p-6">
                        <div className="text-center py-8">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-5xl text-green-500 mb-4" />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                Eliminación Completada
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Se eliminaron exitosamente <strong>{searchResults.deleted}</strong> clientes
                            </p>
                            <button
                                onClick={handleReset}
                                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Nueva Búsqueda
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex items-start gap-2">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 mt-0.5" />
                        <div>
                            <h5 className="font-semibold text-red-900 mb-1">Error</h5>
                            <p className="text-sm text-red-800">{error}</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SpamCleanup;

